import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Loader2,
  Plus,
  QrCode,
  RefreshCw,
  ShieldCheck,
  Users
} from 'lucide-react';
import { apiRequest, apiDownload } from '../../services/api';
import { TENANT } from '../../config';
import { Button } from '../../components/new-components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/new-components/ui/dialog';
import EventFunnelFormEditor, { FunnelField } from './EventFunnelFormEditor';

type EventFunnel = {
  id: string;
  slug: string;
  event_name: string;
  public_title: string;
  public_subtitle?: string;
  platform_blurb?: string;
  owner_name?: string;
  team_member?: string;
  default_host_name?: string;
  default_host_email?: string;
  qr_url: string;
  is_active: boolean;
  scan_count: number;
  lead_count: number;
  booking_count: number;
  pending_access_count: number;
  approved_access_count: number;
  // Present on the API's response all along; the type simply never said so,
  // which is why nothing could edit them.
  conference_id?: string | null;
  form_config?: Record<string, any>;
  landing_copy?: Record<string, any>;
};

type EventLead = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  investor_type?: string;
  investment_range?: string;
  primary_interest?: string;
  lead_status: string;
  deal_room_access_status: string;
  investor_verification_status: string;
  source?: string;
  medium?: string;
  latest_meeting_type?: string;
  latest_booking_starts_at?: string;
  latest_booking_status?: string;
  linked_user_id?: string;
  created_at: string;
};

type EventBooking = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  meeting_type: string;
  starts_at: string;
  status: string;
};

/**
 * What this screen calls things. Mirrors defaultNames() in the server's
 * eventFunnelCopy.js: a tenant that does not sell investments never sees
 * "Investor Relations" as the owner of a conference funnel, nor "Deal Room" as
 * the name of the screen.
 */
const SCREEN = TENANT.features.investing
  ? {
      title: 'Event Deal Room Funnels',
      blurb: 'QR campaigns, event leads, native booking, and manual deal-room access review.',
      owner: 'Investor Relations',
      publicTitle: `Access the ${TENANT.branding.name} Deal Room`,
      publicSubtitle: 'Request access and book a private intro call with the team.',
      platformBlurb: `${TENANT.branding.name} provides a controlled investor workflow for event leads, qualification, and deal-room review.`,
      eventPlaceholder: 'Proof of Talk 2026',
      slugPlaceholder: 'proof-of-talk-2026',
      accessColumn: 'Deal Room',
      bookingContactColumn: 'Investor'
    }
  : {
      title: 'Event Funnels',
      blurb: 'QR campaigns, event leads, native booking, and follow-up review.',
      owner: 'Outreach Team',
      publicTitle: `Connect with ${TENANT.branding.name}`,
      publicSubtitle: 'Tell us about your community and book a short call with our team.',
      platformBlurb: `${TENANT.branding.name} brings dentistry, podiatry, optometry, and audiology directly to long-term care communities. Share a few details and we will follow up.`,
      eventPlaceholder: 'FHCA Annual 2026',
      slugPlaceholder: 'fhca-annual-2026',
      accessColumn: 'Review',
      bookingContactColumn: 'Contact'
    };

const emptyCreateForm = {
  event_name: '',
  slug: '',
  public_title: '',
  public_subtitle: '',
  platform_blurb: '',
  owner_name: SCREEN.owner,
  team_member: '',
  default_host_name: SCREEN.owner,
  default_host_email: ''
};

const statusColor = (status: string) => {
  switch (status) {
    case 'approved':
    case 'meeting_booked':
    case 'completed':
    case 'call_completed':
      return 'bg-emerald-100 text-emerald-800';
    case 'declined':
    case 'cancelled':
    case 'no_show':
      return 'bg-red-100 text-red-800';
    case 'pending_review':
    case 'requested':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatDateTime = (value?: string) => {
  if (!value) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
};

const EventFunnels: React.FC = () => {
  const [funnels, setFunnels] = useState<EventFunnel[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [leads, setLeads] = useState<EventLead[]>([]);
  const [bookings, setBookings] = useState<EventBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingForm, setEditingForm] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // The same dialog serves both. One set of labels and one set of fields means
  // create and edit cannot drift apart, which is how a funnel ends up with a
  // property only reachable at creation time.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({ ...emptyCreateForm });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const saveFunnelForm = async (fields: FunnelField[], heroImageUrl: string | null) => {
    if (!selected) return;
    setSavingForm(true);
    try {
      // form_config carries the fields; landing_copy carries the image, since
      // that is where the rest of the page's presentation already lives.
      // PUT, not PATCH. adminEventFunnels.js defines `router.put('/:id')` and
      // no PATCH for a funnel -- a PATCH here matches nothing and 404s, which
      // is how the form editor shipped inert.
      const response = await apiRequest('PUT', `/admin/event-funnels/${selected.id}`, {
        form_config: { ...(selected.form_config || {}), fields },
        landing_copy: { ...(selected.landing_copy || {}), hero_image_url: heroImageUrl },
      });
      if (!response.success) {
        window.alert(response.error || 'Could not save the form.');
        return;
      }
      setEditingForm(false);
      await loadFunnels();
    } finally {
      setSavingForm(false);
    }
  };

  const selected = useMemo(() => funnels.find(funnel => funnel.id === selectedId) || funnels[0], [funnels, selectedId]);

  const loadFunnels = useCallback(async () => {
    setLoading(true);
    const response = await apiRequest<EventFunnel[]>('GET', '/admin/event-funnels');
    if (response.success && response.data) {
      setFunnels(response.data);
      if (!selectedId && response.data[0]) setSelectedId(response.data[0].id);
    }
    setLoading(false);
  }, [selectedId]);

  const loadDetails = useCallback(async (id: string) => {
    if (!id) return;
    setDetailLoading(true);
    const [leadRes, bookingRes] = await Promise.all([
      apiRequest<EventLead[]>('GET', `/admin/event-funnels/${id}/leads`),
      apiRequest<EventBooking[]>('GET', `/admin/event-funnels/${id}/bookings`)
    ]);
    if (leadRes.success && leadRes.data) setLeads(leadRes.data);
    if (bookingRes.success && bookingRes.data) setBookings(bookingRes.data);
    setDetailLoading(false);
  }, []);

  useEffect(() => { loadFunnels(); }, [loadFunnels]);
  useEffect(() => { if (selected?.id) loadDetails(selected.id); }, [loadDetails, selected?.id]);

  const copyQrUrl = async () => {
    if (!selected?.qr_url) return;
    await navigator.clipboard.writeText(selected.qr_url);
    setMessage('QR campaign URL copied');
    window.setTimeout(() => setMessage(''), 2500);
  };

  const downloadQr = async (format: 'svg' | 'png') => {
    if (!selected) return;
    try {
      // Goes through the shared client so an expired access token is refreshed
      // and the request retried. A raw fetch here skipped that, which made this
      // the only control on the page that broke when the token aged out.
      const blob = await apiDownload(`/admin/event-funnels/${selected.id}/qr.${format}`);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${selected.slug}-qr.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      // Say which failure it was. "QR download failed" sent whoever hit this
      // looking at the QR encoder, which was never the problem.
      const status = error?.response?.status;
      setMessage(
        status === 401 ? 'Session expired — sign in again to download the QR'
          : status === 404 ? 'QR download failed: funnel not found'
            : status === 429 ? 'Too many requests — wait a moment and retry'
              : 'QR download failed'
      );
      window.setTimeout(() => setMessage(''), 4000);
    }
  };

  const openEditor = (funnel: EventFunnel) => {
    setCreateForm({
      event_name: funnel.event_name || '',
      slug: funnel.slug || '',
      public_title: funnel.public_title || '',
      public_subtitle: funnel.public_subtitle || '',
      platform_blurb: funnel.platform_blurb || '',
      owner_name: funnel.owner_name || '',
      team_member: funnel.team_member || '',
      default_host_name: funnel.default_host_name || '',
      default_host_email: funnel.default_host_email || '',
    });
    setEditingId(funnel.id);
    setShowCreate(true);
  };

  const saveFunnel = async () => {
    setSaving(true);
    // Defaults are applied on creation only. Filling a blank subtitle on every
    // save would silently restore boilerplate an administrator had cleared on
    // purpose.
    const body = editingId
      ? { ...createForm }
      : {
        ...createForm,
        public_title: createForm.public_title || SCREEN.publicTitle,
        public_subtitle: createForm.public_subtitle || SCREEN.publicSubtitle,
        platform_blurb: createForm.platform_blurb || SCREEN.platformBlurb,
      };

    const response = editingId
      ? await apiRequest<EventFunnel>('PUT', `/admin/event-funnels/${editingId}`, body)
      : await apiRequest<EventFunnel>('POST', '/admin/event-funnels', body);

    if (response.success && response.data) {
      setShowCreate(false);
      setEditingId(null);
      setCreateForm({ ...emptyCreateForm });
      setSelectedId(response.data.id);
      await loadFunnels();
    } else {
      setMessage(response.message || response.error
        || `Event funnel could not be ${editingId ? 'saved' : 'created'}`);
    }
    setSaving(false);
  };

  const updateLead = async (leadId: string, changes: Partial<EventLead>) => {
    const response = await apiRequest<EventLead>('PATCH', `/admin/event-funnels/leads/${leadId}`, changes);
    if (response.success && selected?.id) {
      await Promise.all([loadDetails(selected.id), loadFunnels()]);
      setMessage('Lead status updated');
      window.setTimeout(() => setMessage(''), 2500);
    }
  };

  const updateBookingStatus = async (bookingId: string, status: string) => {
    const response = await apiRequest<EventBooking>('PATCH', `/admin/event-funnels/bookings/${bookingId}/status`, { status });
    if (response.success && selected?.id) {
      await Promise.all([loadDetails(selected.id), loadFunnels()]);
      setMessage('Booking status updated');
      window.setTimeout(() => setMessage(''), 2500);
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">{SCREEN.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{SCREEN.blurb}</p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" onClick={loadFunnels} disabled={loading} className="justify-center">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} className="justify-center">
            <Plus className="mr-1.5 h-4 w-4" /> New Funnel
          </Button>
        </div>
      </div>

      {message && <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{message}</div>}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid min-w-0 gap-5 sm:gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
          <div className="space-y-3 min-w-0">
            {funnels.map(funnel => (
              <button
                key={funnel.id}
                onClick={() => setSelectedId(funnel.id)}
                className={`w-full rounded-lg border bg-white p-4 text-left transition ${selected?.id === funnel.id ? 'border-[#242424] shadow-sm' : 'border-border hover:border-gray-400'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-[#242424]">{funnel.event_name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">/{funnel.slug}</div>
                  </div>
                  <Badge className={funnel.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}>{funnel.is_active ? 'active' : 'paused'}</Badge>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-center text-xs text-muted-foreground">
                  <span className="rounded-md bg-gray-50 px-2 py-2"><strong className="block text-[#242424]">{funnel.scan_count}</strong>Scans</span>
                  <span className="rounded-md bg-gray-50 px-2 py-2"><strong className="block text-[#242424]">{funnel.lead_count}</strong>Leads</span>
                  <span className="rounded-md bg-gray-50 px-2 py-2"><strong className="block text-[#242424]">{funnel.booking_count}</strong>Calls</span>
                </div>
              </button>
            ))}
            {!funnels.length && (
              <Card>
                <CardContent className="p-6 text-sm text-muted-foreground">No event funnels have been created yet.</CardContent>
              </Card>
            )}
          </div>

          {selected && (
            <EventFunnelFormEditor
              open={editingForm}
              onClose={() => setEditingForm(false)}
              conferenceId={(selected as any).conference_id || null}
              initialFields={((selected.form_config || {}) as any).fields || null}
              initialHero={((selected.landing_copy || {}) as any).hero_image_url || null}
              saving={savingForm}
              onSave={saveFunnelForm}
            />
          )}

          {selected && (
            <div className="space-y-5 sm:space-y-6 min-w-0">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <CardTitle className="flex items-center gap-2 text-lg text-[#242424]"><QrCode className="h-5 w-5" /> {selected.event_name}</CardTitle>
                      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{selected.public_title}</p>
                    </div>
                    <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 w-full sm:w-auto">
                      <a href={selected.qr_url} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" className="w-full sm:w-auto justify-center"><ExternalLink className="mr-1.5 h-4 w-4" /> Open</Button>
                      </a>
                      <Button variant="outline" size="sm" onClick={copyQrUrl} className="justify-center"><Copy className="mr-1.5 h-4 w-4" /> Copy URL</Button>
                      <Button variant="outline" size="sm" onClick={() => downloadQr('svg')} className="justify-center"><Download className="mr-1.5 h-4 w-4" /> SVG</Button>
                      <Button variant="outline" size="sm" onClick={() => downloadQr('png')} className="justify-center"><Download className="mr-1.5 h-4 w-4" /> PNG</Button>
                      <Button variant="outline" size="sm" onClick={() => openEditor(selected)} className="justify-center">Edit details</Button>
                      <Button variant="outline" size="sm" onClick={() => setEditingForm(true)} className="justify-center">Edit form</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <div className="rounded-md bg-gray-50 p-4"><QrCode className="mb-2 h-4 w-4 text-muted-foreground" /><div className="text-2xl font-semibold">{selected.scan_count}</div><div className="text-xs text-muted-foreground">QR scans</div></div>
                    <div className="rounded-md bg-gray-50 p-4"><Users className="mb-2 h-4 w-4 text-muted-foreground" /><div className="text-2xl font-semibold">{selected.lead_count}</div><div className="text-xs text-muted-foreground">Leads</div></div>
                    <div className="rounded-md bg-gray-50 p-4"><CalendarDays className="mb-2 h-4 w-4 text-muted-foreground" /><div className="text-2xl font-semibold">{selected.booking_count}</div><div className="text-xs text-muted-foreground">Booked calls</div></div>
                    <div className="rounded-md bg-gray-50 p-4"><ShieldCheck className="mb-2 h-4 w-4 text-muted-foreground" /><div className="text-2xl font-semibold">{selected.pending_access_count}</div><div className="text-xs text-muted-foreground">Pending access</div></div>
                    <div className="rounded-md bg-gray-50 p-4"><CheckCircle2 className="mb-2 h-4 w-4 text-muted-foreground" /><div className="text-2xl font-semibold">{selected.approved_access_count}</div><div className="text-xs text-muted-foreground">Approved</div></div>
                  </div>
                  <div className="mt-4 rounded-md bg-gray-50 px-3 py-2 font-mono text-xs text-muted-foreground break-all">{selected.qr_url}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-[#242424]">Lead Inbox</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {detailLoading ? (
                    <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                  ) : (
                    <>
                      <div className="md:hidden divide-y divide-border">
                        {leads.map(lead => (
                          <div key={lead.id} className="space-y-3 p-4">
                            <div className="min-w-0">
                              <div className="font-medium text-[#242424] truncate">{lead.first_name} {lead.last_name}</div>
                              <div className="text-xs text-muted-foreground break-all">{lead.email}</div>
                              {lead.phone && <div className="text-xs text-muted-foreground">{lead.phone}</div>}
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                              <div><span className="block font-medium text-[#242424]">Profile</span>{lead.investor_type || 'Unknown type'}<br />{lead.investment_range || 'No range'}</div>
                              <div><span className="block font-medium text-[#242424]">Booking</span>{lead.latest_meeting_type || 'Not booked'}<br />{formatDateTime(lead.latest_booking_starts_at)}</div>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <select value={lead.lead_status} onChange={e => updateLead(lead.id, { lead_status: e.target.value })} className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm">
                                <option value="form_submitted">Form submitted</option>
                                <option value="meeting_booked">Meeting booked</option>
                                <option value="call_completed">Call completed</option>
                                <option value="nurture">Nurture</option>
                                <option value="not_fit">Not fit</option>
                              </select>
                              <select value={lead.deal_room_access_status} onChange={e => updateLead(lead.id, { deal_room_access_status: e.target.value })} className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm">
                                <option value="requested">Requested</option>
                                <option value="pending_review">Pending review</option>
                                <option value="approved">Approved</option>
                                <option value="declined">Declined</option>
                                <option value="revoked">Revoked</option>
                              </select>
                              <select value={lead.investor_verification_status} onChange={e => updateLead(lead.id, { investor_verification_status: e.target.value })} className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm">
                                <option value="basic_info_captured">Basic info captured</option>
                                <option value="needs_registration">Needs registration</option>
                                <option value="kyc_required">KYC required</option>
                                <option value="verified">Verified</option>
                                <option value="ineligible">Ineligible</option>
                              </select>
                            </div>
                          </div>
                        ))}
                        {!leads.length && <div className="p-8 text-center text-sm text-muted-foreground">No leads yet.</div>}
                      </div>

                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full min-w-[980px]">
                          <thead>
                            <tr className="border-b border-border text-left text-xs text-muted-foreground">
                              <th className="p-4">Lead</th>
                              <th className="p-4">Profile</th>
                              <th className="p-4">Booking</th>
                              <th className="p-4">Lead Status</th>
                              <th className="p-4">{SCREEN.accessColumn}</th>
                              <th className="p-4">Verification</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leads.map(lead => (
                              <tr key={lead.id} className="border-b border-border align-top hover:bg-muted/40">
                            <td className="p-4">
                              <div className="font-medium text-[#242424]">{lead.first_name} {lead.last_name}</div>
                              <div className="text-xs text-muted-foreground">{lead.email}</div>
                              {lead.phone && <div className="text-xs text-muted-foreground">{lead.phone}</div>}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              <div>{lead.investor_type || 'Unknown type'}</div>
                              <div>{lead.investment_range || 'No range'}</div>
                              <div>{lead.primary_interest || 'No interest'}</div>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              <div>{lead.latest_meeting_type || 'Not booked'}</div>
                              <div>{formatDateTime(lead.latest_booking_starts_at)}</div>
                              {lead.latest_booking_status && <Badge className={statusColor(lead.latest_booking_status)}>{lead.latest_booking_status}</Badge>}
                            </td>
                            <td className="p-4">
                              <select value={lead.lead_status} onChange={e => updateLead(lead.id, { lead_status: e.target.value })} className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
                                <option value="form_submitted">Form submitted</option>
                                <option value="meeting_booked">Meeting booked</option>
                                <option value="call_completed">Call completed</option>
                                <option value="nurture">Nurture</option>
                                <option value="not_fit">Not fit</option>
                              </select>
                            </td>
                            <td className="p-4">
                              <select value={lead.deal_room_access_status} onChange={e => updateLead(lead.id, { deal_room_access_status: e.target.value })} className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
                                <option value="requested">Requested</option>
                                <option value="pending_review">Pending review</option>
                                <option value="approved">Approved</option>
                                <option value="declined">Declined</option>
                                <option value="revoked">Revoked</option>
                              </select>
                            </td>
                            <td className="p-4">
                              <select value={lead.investor_verification_status} onChange={e => updateLead(lead.id, { investor_verification_status: e.target.value })} className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
                                <option value="basic_info_captured">Basic info captured</option>
                                <option value="needs_registration">Needs registration</option>
                                <option value="kyc_required">KYC required</option>
                                <option value="verified">Verified</option>
                                <option value="ineligible">Ineligible</option>
                              </select>
                            </td>
                              </tr>
                            ))}
                            {!leads.length && (
                              <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">No leads yet.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-[#242424]">Booked Calls</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="md:hidden divide-y divide-border">
                    {bookings.map(item => (
                      <div key={item.id} className="space-y-3 p-4">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{item.first_name} {item.last_name}</div>
                          <div className="text-xs text-muted-foreground break-all">{item.email}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                          <div><span className="block font-medium text-[#242424]">Meeting</span>{item.meeting_type}</div>
                          <div><span className="block font-medium text-[#242424]">Time</span>{formatDateTime(item.starts_at)}</div>
                        </div>
                        <select value={item.status} onChange={e => updateBookingStatus(item.id, e.target.value)} className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm">
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="no_show">No show</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    ))}
                    {!bookings.length && <div className="p-8 text-center text-sm text-muted-foreground">No calls booked yet.</div>}
                  </div>

                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full min-w-[760px]">
                      <thead>
                        <tr className="border-b border-border text-left text-xs text-muted-foreground">
                          <th className="p-4">{SCREEN.bookingContactColumn}</th>
                          <th className="p-4">Meeting</th>
                          <th className="p-4">Time</th>
                          <th className="p-4">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map(item => (
                          <tr key={item.id} className="border-b border-border hover:bg-muted/40">
                            <td className="p-4"><div className="font-medium">{item.first_name} {item.last_name}</div><div className="text-xs text-muted-foreground">{item.email}</div></td>
                            <td className="p-4 text-sm text-muted-foreground">{item.meeting_type}</td>
                            <td className="p-4 text-sm text-muted-foreground">{formatDateTime(item.starts_at)}</td>
                            <td className="p-4">
                              <select value={item.status} onChange={e => updateBookingStatus(item.id, e.target.value)} className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
                                <option value="confirmed">Confirmed</option>
                                <option value="completed">Completed</option>
                                <option value="no_show">No show</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                        {!bookings.length && <tr><td colSpan={4} className="p-8 text-center text-sm text-muted-foreground">No calls booked yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={showCreate}
        onOpenChange={(open) => {
          setShowCreate(open);
          // Leaving `editingId` set would turn the next "New funnel" into a
          // silent overwrite of the one edited last.
          if (!open) { setEditingId(null); setCreateForm({ ...emptyCreateForm }); }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit event funnel' : 'New event funnel'}</DialogTitle>
          </DialogHeader>
          {editingId && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Changing the slug changes the public URL, and every QR code
              already printed with the old one stops working. The conference ID
              does not change, so previously captured leads stay attributable
              either way.
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">Event name<Input value={createForm.event_name} onChange={e => setCreateForm(prev => ({ ...prev, event_name: e.target.value }))} placeholder={SCREEN.eventPlaceholder} /></label>
            <label className="text-sm font-medium text-gray-700">Slug<Input value={createForm.slug} onChange={e => setCreateForm(prev => ({ ...prev, slug: e.target.value }))} placeholder={SCREEN.slugPlaceholder} /></label>
            <label className="text-sm font-medium text-gray-700 sm:col-span-2">Public title<Input value={createForm.public_title} onChange={e => setCreateForm(prev => ({ ...prev, public_title: e.target.value }))} placeholder={SCREEN.publicTitle} /></label>
            <label className="text-sm font-medium text-gray-700 sm:col-span-2">Subtitle<Input value={createForm.public_subtitle} onChange={e => setCreateForm(prev => ({ ...prev, public_subtitle: e.target.value }))} /></label>
            <label className="text-sm font-medium text-gray-700 sm:col-span-2">Platform blurb<Input value={createForm.platform_blurb} onChange={e => setCreateForm(prev => ({ ...prev, platform_blurb: e.target.value }))} /></label>
            <label className="text-sm font-medium text-gray-700">Owner<Input value={createForm.owner_name} onChange={e => setCreateForm(prev => ({ ...prev, owner_name: e.target.value }))} /></label>
            <label className="text-sm font-medium text-gray-700">Team member<Input value={createForm.team_member} onChange={e => setCreateForm(prev => ({ ...prev, team_member: e.target.value }))} /></label>
            <label className="text-sm font-medium text-gray-700">Host name<Input value={createForm.default_host_name} onChange={e => setCreateForm(prev => ({ ...prev, default_host_name: e.target.value }))} /></label>
            <label className="text-sm font-medium text-gray-700">Host email<Input value={createForm.default_host_email} onChange={e => setCreateForm(prev => ({ ...prev, default_host_email: e.target.value }))} /></label>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={saveFunnel} disabled={saving || !createForm.event_name.trim()}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Create Funnel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventFunnels;